import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Heart, LogOut, Trash2, User as UserIcon, Mail, Calendar, ShieldCheck } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSheet({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = React.useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/auth/favorites`);
      setFavorites(data || []);
    } catch {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/auth/favorites/${id}`);
      setFavorites((arr) => arr.filter((f) => f.id !== id));
      toast({ title: "Favorito eliminado" });
    } catch {
      toast({ title: "Error eliminando" });
    }
  };

  if (!user) return null;
  const initials = (user.name || "").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "U";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md p-0 overflow-y-auto">
        <div className="bg-gradient-to-br from-[#0e4a7a] via-[#1d6cb1] to-[#2a83ce] p-6 text-white">
          <SheetHeader>
            <SheetTitle className="text-white text-xl">Mi cuenta</SheetTitle>
            <SheetDescription className="text-blue-100">Gestiona tus datos y favoritos</SheetDescription>
          </SheetHeader>
          <div className="mt-5 flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center font-extrabold text-xl">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-lg truncate">{user.name}</p>
              <p className="text-xs text-blue-100 flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {user.email}</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0e4a7a] flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" /> Mis favoritos</h3>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-[#0e4a7a] rounded-full font-bold">{favorites.length}</span>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">Cargando...</p>
          ) : favorites.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              <Heart className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>Aun no tienes números favoritos.</p>
              <p className="text-xs mt-1">Genera palés y guárdalos aquí.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {favorites.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-2 bg-blue-50/50 border border-blue-100 rounded-xl px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0e4a7a] truncate">{f.label}</p>
                    <p className="text-xs text-gray-500 font-mono">{(f.numbers || []).join(", ")}</p>
                  </div>
                  <button onClick={() => remove(f.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={() => { logout(); onOpenChange(false); toast({ title: "Sesi\u00f3n cerrada" }); }}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>

          {user.is_admin && (
            <Link
              to="/admin"
              onClick={() => onOpenChange(false)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#0e1b4a] to-[#0e4a7a] text-white font-semibold hover:shadow-lg transition"
            >
              <ShieldCheck className="w-4 h-4" /> Panel administrador
            </Link>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
